import {
  computed,
  onUnmounted,
  ref,
  watch,
  type WatchSource,
} from 'vue'

const busyTokens = ref(0)
const busyLabelKey = ref('common.saving')

/** Read-only state for the single app-wide form busy overlay. */
export function useFormBusyState() {
  return {
    show: computed(() => busyTokens.value > 0),
    labelKey: computed(() => busyLabelKey.value),
  }
}

/**
 * Drive the shared full-screen busy overlay.
 * Pass a boolean ref/computed (e.g. `saving`) or call acquire/release manually.
 */
export function useFormBusy(
  source?: WatchSource<boolean>,
  labelKey = 'common.saving',
) {
  let held = false

  function acquire(key = labelKey) {
    if (held) {
      busyLabelKey.value = key
      return
    }
    held = true
    busyLabelKey.value = key
    busyTokens.value += 1
  }

  function release() {
    if (!held) return
    held = false
    busyTokens.value = Math.max(0, busyTokens.value - 1)
  }

  if (source) {
    watch(
      source,
      (active) => {
        if (active) acquire()
        else release()
      },
      { immediate: true },
    )
    onUnmounted(release)
  }

  async function run<T>(fn: () => Promise<T>, key = labelKey): Promise<T> {
    acquire(key)
    try {
      return await fn()
    } finally {
      release()
    }
  }

  return { acquire, release, run }
}

/** Brief full-screen busy flash (in-page tab switches, etc.). */
export function pulseScreenBusy(labelKey = 'common.loading', ms = 360): void {
  busyLabelKey.value = labelKey
  busyTokens.value += 1
  window.setTimeout(() => {
    busyTokens.value = Math.max(0, busyTokens.value - 1)
  }, ms)
}
